import moment from 'moment-timezone';
import { formatTimezoneDisplay, convertBookingTimeWithUserTimezone, convertUTCToUserTimezone } from '../../../../../shared-lib/functions/moment-common-function';
import { getCountryForTimezone } from 'countries-and-timezones';

/**
 * Format date and time in the viewer's timezone (client Account / consultant Availability).
 * Do not use Date#toLocaleTimeString — that prints browser local time and breaks Qatar vs IST.
 */
export const formatDateTime = (dateStr: string, userTimezone?: string): string => {
  if (!dateStr) return '';

  try {
    const isUTC =
      dateStr.includes('Z') ||
      dateStr.includes('UTC') ||
      dateStr.includes('GMT') ||
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(dateStr);

    let m: moment.Moment;
    if (isUTC && userTimezone) {
      m = moment.utc(dateStr).tz(userTimezone);
    } else if (userTimezone) {
      m = moment.tz(dateStr, userTimezone);
    } else {
      m = moment(dateStr);
    }

    if (!m.isValid()) return '';

    // DD/MM/YYYY - hh:mm AM/PM in the target timezone wall clock
    return m.format('DD/MM/YYYY - hh:mm A');
  } catch (error) {
    console.error('Error formatting date time:', error, { dateStr, userTimezone });
    const m = userTimezone ? moment.utc(dateStr).tz(userTimezone) : moment(dateStr);
    return m.isValid() ? m.format('DD/MM/YYYY - hh:mm A') : '';
  }
};

export const resolveBookingTimezone = (booking?: {
  timezone?: string;
  bookingTimezone?: string;
  timeZone?: string;
  bookingTimeZone?: string;
} | null): string =>
  booking?.timezone ||
  booking?.bookingTimezone ||
  booking?.timeZone ||
  booking?.bookingTimeZone ||
  'Asia/Calcutta';

const parseApiDateTimeMoment = (
  dateTime: string,
  userTimezone: string,
  bookingTimezone?: string
): moment.Moment => {
  const isUTC =
    dateTime.includes('Z') ||
    dateTime.includes('UTC') ||
    dateTime.includes('GMT') ||
    !!dateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/);

  return isUTC
    ? moment.utc(dateTime).tz(userTimezone)
    : moment.tz(dateTime, bookingTimezone || 'Asia/Calcutta').tz(userTimezone);
};

/**
 * End moment aligned with Booking Details display (formattedTimeSlot end + converted date).
 */
export const getBookingEndMomentFromDisplay = (
  bookingDate: string,
  bookingTimeSlot: string,
  formattedTimeSlot: string,
  userTimezone: string,
  bookingTimezone?: string
): moment.Moment | null => {
  if (!formattedTimeSlot || formattedTimeSlot === '-') return null;

  const [startTimeStr, endTimeStr] = formattedTimeSlot.split(' - ');
  if (!startTimeStr?.trim() || !endTimeStr?.trim()) return null;

  try {
    const isFullDateTime =
      !!bookingTimeSlot &&
      bookingTimeSlot.includes('T') &&
      !!bookingTimeSlot.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);

    let displayDateMoment: moment.Moment;
    if (isFullDateTime) {
      displayDateMoment = parseApiDateTimeMoment(bookingTimeSlot, userTimezone, bookingTimezone);
    } else {
      const isUTC =
        bookingDate.includes('Z') ||
        bookingDate.includes('UTC') ||
        bookingDate.includes('GMT') ||
        !!bookingDate.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/);

      if (isUTC && userTimezone) {
        const { convertedDate } = convertUTCToUserTimezone(bookingDate, bookingDate, userTimezone);
        displayDateMoment = moment(convertedDate).tz(userTimezone);
      } else if (userTimezone) {
        const { convertedDate } = convertBookingTimeWithUserTimezone(
          bookingDate,
          bookingDate,
          bookingTimezone || 'Asia/Calcutta',
          userTimezone
        );
        displayDateMoment = moment(convertedDate).tz(userTimezone);
      } else {
        displayDateMoment = moment(bookingDate);
      }
    }

    const bookingDateStr = displayDateMoment.format('YYYY-MM-DD');
    const startMoment = moment.tz(
      `${bookingDateStr} ${startTimeStr.trim()}`,
      'YYYY-MM-DD h:mm a',
      userTimezone
    );
    let endMoment = moment.tz(
      `${bookingDateStr} ${endTimeStr.trim()}`,
      'YYYY-MM-DD h:mm a',
      userTimezone
    );

    if (!startMoment.isValid() || !endMoment.isValid()) return null;

    if (endMoment.isBefore(startMoment) || endMoment.isSame(startMoment)) {
      endMoment = endMoment.clone().add(1, 'day');
    }

    return endMoment;
  } catch (error) {
    console.error('Error getting booking end moment from display:', error, {
      bookingDate,
      bookingTimeSlot,
      formattedTimeSlot,
      bookingTimezone,
      userTimezone,
    });
    return null;
  }
};

/**
 * Format booking time slot with timezone conversion
 */
export const formatTimeSlot = (
  bookingDate: string,
  bookingTimeSlot: string,
  userTimezone: string,
  bookingTimezone?: string,
  durationMinutes?: number,
  endDateTime?: string
): string => {
  if (!bookingDate || !bookingTimeSlot) return '-';
  
  try {
    // Check if bookingTimeSlot is a full datetime string (from getdetailId API)
    // Format: "2025-12-30T08:30:51.335" or "2025-12-30T08:30:51"
    const isFullDateTime = bookingTimeSlot.includes('T') && bookingTimeSlot.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);
    
    if (isFullDateTime) {
      const startMoment = parseApiDateTimeMoment(bookingTimeSlot, userTimezone, bookingTimezone);
      const startTime = startMoment.format('hh:mm a');

      let endMoment: moment.Moment;
      if (endDateTime) {
        endMoment = parseApiDateTimeMoment(endDateTime, userTimezone, bookingTimezone);
      } else {
        const duration = durationMinutes || 45;
        endMoment = startMoment.clone().add(duration, 'minutes');
      }

      const endTime = endMoment.format('hh:mm a');
      return `${startTime} - ${endTime}`;
    }
    
    // Check if the booking time is in UTC format (including ISO format without timezone)
    const isUTC = bookingDate.includes('Z') || bookingDate.includes('UTC') || bookingDate.includes('GMT') || 
                  bookingTimeSlot.includes('UTC') || bookingTimeSlot.includes('GMT') ||
                  bookingDate.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/);
    
    if (isUTC) {
      // Use UTC-specific conversion function
      const { displayTimeSlot } = convertUTCToUserTimezone(
        bookingDate,
        bookingTimeSlot,
        userTimezone
      );
      return displayTimeSlot;
    } else {
      // Use the enhanced conversion function that handles all edge cases
      const { displayTimeSlot } = convertBookingTimeWithUserTimezone(
        bookingDate,
        bookingTimeSlot,
        bookingTimezone || 'Asia/Calcutta', // Booking timezone
        userTimezone // User timezone from GetBasicProfile API
      );
      return displayTimeSlot;
    }
  } catch (error) {
    console.error('Error converting booking time slot in BookingDetail:', error, {
      bookingDate,
      bookingTimeSlot,
      bookingTimezone,
      userTimezone
    });
    return bookingTimeSlot;
  }
};

/**
 * Format timezone string with UTC offset and country name
 */
export const formatTimezoneTZ = (tz: string): string => {
  try {
    if (!tz) return '';
    
    // Get the UTC offset and city name using formatTimezoneDisplay format
    const momentTz = moment.tz(tz);
    const offset = momentTz.format('Z');
    const timezoneName = tz.split('/').pop()?.replace('_', ' ') || tz;
    
    // Format timezone with country name instead of continent (e.g., "America/Edmonton" -> "Canada/Edmonton")
    let formattedTimezone = tz;
    const country: any = getCountryForTimezone(tz);
    if (country?.name) {
      formattedTimezone = tz.replace(/.*\//, country.name + "/");
    }
    
    // Return format: (UTC+offset) CityName Country/Timezone
    return `(UTC${offset}) ${timezoneName} ${formattedTimezone}`;
  } catch {
    // Fallback to original formatTimezoneDisplay + timezone
    try {
      return `${formatTimezoneDisplay(tz)} ${tz}`;
    } catch {
      return tz;
    }
  }
};

/** Booking-detail timezone line: (UTC+05:30) India/Calcutta */
export const formatBookingTimezoneLine = (tz: string): string => {
  try {
    if (!tz) return '';
    const zone = moment.tz.zone(tz) ? tz : 'Asia/Calcutta';
    const offset = moment.tz(zone).format('Z');
    const country: any = getCountryForTimezone(zone);
    const city = zone.split('/').pop()?.replace(/_/g, ' ') || zone;
    const place = country?.name ? `${country.name}/${city}` : city;
    return `(UTC${offset}) ${place}`;
  } catch {
    return formatTimezoneTZ(tz);
  }
};

/** Safe numeric parse for billing fields — returns fallback when value is null, undefined, empty, or NaN. */
export const toSafeBillingNumber = (value: unknown, fallback = 0): number => {
  if (value == null || value === '') return fallback;
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

/**
 * Same Total calculation as BillingDetailsCard.
 * Returns null when booking has no price/totalAmount fields (omit Amount from calendar).
 *
 * Note: API sometimes stores totalAmount equal to base price while processingFee > 0.
 * In that case Billing UI still shows fees separately — calendar Total must be price + fee.
 */
export const resolveBillingDisplayTotal = (billing: {
  price?: unknown;
  processingFee?: unknown;
  totalAmount?: unknown;
  baseAmount?: unknown;
  taxAmount?: unknown;
} | null | undefined): number | null => {
  if (!billing) return null;

  const hasPrice =
    (billing.price != null && billing.price !== '') ||
    (billing.baseAmount != null && billing.baseAmount !== '');
  const hasTotal = billing.totalAmount != null && billing.totalAmount !== '';
  if (!hasPrice && !hasTotal) return null;

  const consultationAmount = toSafeBillingNumber(
    billing.price != null && billing.price !== '' ? billing.price : billing.baseAmount
  );
  const serviceFees = toSafeBillingNumber(billing.processingFee);
  const tax = toSafeBillingNumber(billing.taxAmount);
  const parsedTotal = toSafeBillingNumber(billing.totalAmount, NaN);

  let displayTotalAmount =
    hasTotal && Number.isFinite(parsedTotal)
      ? parsedTotal
      : consultationAmount + serviceFees + tax;

  // totalAmount is often only base while fee and/or tax exist → calendar Amount = price + fee + tax
  const sumWithFees = consultationAmount + serviceFees + tax;
  if ((serviceFees > 0 || tax > 0) && sumWithFees > displayTotalAmount + 0.009) {
    displayTotalAmount = sumWithFees;
  }

  if (!(displayTotalAmount > 0)) return null;
  return displayTotalAmount;
};

/** Format a billing amount for display; never throws on null/invalid input. */
export const formatBillingCurrency = (value: unknown, fallback = 0): string => {
  return `$${toSafeBillingNumber(value, fallback).toFixed(2)}`;
};

/** Safe text for billing labels; returns fallback when value is null/empty. */
export const toSafeBillingText = (value: unknown, fallback = ''): string => {
  if (value == null) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

/** Format duration from timeInMinutes; returns fallback when value is null/invalid. */
export const formatBillingDuration = (value: unknown, fallback = ''): string => {
  const minutes = toSafeBillingNumber(value, NaN);
  if (!Number.isFinite(minutes) || minutes <= 0) return fallback;
  return `${minutes} min`;
};

const pickAddressText = (...values: unknown[]): string => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (!text || text === 'null' || text === 'undefined') continue;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) continue;
    return text;
  }
  return '';
};

/** One billing address line from an object or a plain string. Skips bare GUIDs. */
export const formatBillingAddressLine = (address: unknown): string => {
  if (!address) return '';
  if (typeof address === 'string') {
    const trimmed = address.trim();
    if (!trimmed) return '';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) return '';
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return formatBillingAddressLine(JSON.parse(trimmed));
      } catch {
        return '';
      }
    }
    return trimmed;
  }
  if (typeof address !== 'object') return '';
  const addr = address as Record<string, unknown>;
  const line1 = pickAddressText(addr.line1, addr.addressLine1, addr.street, addr.address1, addr.line_1);
  if (!line1) return '';
  const parts = [
    line1,
    pickAddressText(addr.line2, addr.addressLine2),
    pickAddressText(addr.city),
    pickAddressText(addr.state, addr.stateName, addr.region),
    pickAddressText(addr.postalCode, addr.zipCode, addr.zip, addr.postal_code),
    pickAddressText(addr.countryName, addr.country),
  ].filter(Boolean);
  return parts.join(', ');
};

/** Address from any transaction row, parent payment, booking payload, or the other user's profile. */
export const resolveBookingBillingAddress = (
  transactions: unknown,
  booking?: unknown,
  userDetails?: unknown
): string => {
  const sources: unknown[] = [];
  const rows = Array.isArray(transactions) ? transactions : [];
  rows.forEach((tx) => {
    if (!tx || typeof tx !== 'object') return;
    const row = tx as Record<string, unknown>;
    sources.push(row.address, row.billingAddress, row.billing_address);
    const parent = row.parentTransaction;
    if (parent && typeof parent === 'object') {
      const parentRow = parent as Record<string, unknown>;
      sources.push(parentRow.address, parentRow.billingAddress);
    }
    if (typeof row.transactionJSON === 'string') {
      try {
        const parsed = JSON.parse(row.transactionJSON) as Record<string, unknown>;
        sources.push(parsed.address, parsed.billingAddress, parsed.billing_address);
      } catch {
        // ignore malformed payment JSON
      }
    }
  });
  const records = [booking, userDetails];
  records.forEach((record) => {
    if (!record || typeof record !== 'object') return;
    const data = record as Record<string, unknown>;
    sources.push(
      data.billingAddress,
      data.address,
      data.clientBillingAddress,
      data.buyerBillingAddress,
      data.billingAddressLine
    );
    ['buyer', 'client', 'user', 'userProfile'].forEach((key) => {
      const nested = data[key];
      if (nested && typeof nested === 'object') sources.push(nested);
    });
  });
  for (const source of sources) {
    const line = formatBillingAddressLine(source);
    if (line) return line;
  }
  return formatBillingAddressLine(userDetails);
};

/**
 * Single-line booking date/time label for reschedule link banners
 * e.g. "Thursday, 19 June 2025 | 11:00 am - 11:30 am (30 min)"
 */
/** Keep time + am/pm (and the range) from wrapping onto orphan lines. */
export const glueTimeMeridiem = (value: string): string =>
  String(value || '')
    .replace(/(\d{1,2}:\d{2})\s+(am|pm)/gi, '$1\u00A0$2')
    .replace(/(am|pm)\s+-\s+(\d{1,2}:\d{2})/gi, '$1\u00A0-\u00A0$2');

export const formatBookingDateTimeDisplay = (
  bookingDate?: string,
  bookingTimeSlot?: string,
  userTimezone?: string,
  bookingTimezone?: string,
  options?: {
    weekdayFormat?: 'long' | 'short';
    durationMinutes?: number;
    endDateTime?: string;
    includeDuration?: boolean;
  }
): string => {
  if (!bookingDate) return '-';

  const weekdayFormat = options?.weekdayFormat || 'long';
  const includeDuration = options?.includeDuration !== false;
  const durationMinutes = options?.durationMinutes;
  const endDateTime = options?.endDateTime;
  const tz = userTimezone || 'Asia/Calcutta';

  try {
    const isFullDateTime = !!(
      bookingTimeSlot &&
      (bookingTimeSlot.includes('T') || bookingTimeSlot.match(/^\d{4}-\d{2}-\d{2}/))
    );
    const isUTC =
      bookingDate.includes('Z') ||
      bookingDate.includes('UTC') ||
      bookingDate.includes('GMT') ||
      bookingTimeSlot?.includes('Z') ||
      bookingTimeSlot?.includes('UTC') ||
      bookingTimeSlot?.includes('GMT') ||
      !!bookingDate.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/) ||
      !!(isFullDateTime && bookingTimeSlot?.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/));

    let convertedMoment: moment.Moment;
    if (isUTC) {
      if (isFullDateTime && bookingTimeSlot) {
        convertedMoment = moment.utc(bookingTimeSlot).tz(tz);
      } else {
        const result = convertUTCToUserTimezone(bookingDate, bookingTimeSlot || '', tz);
        convertedMoment = moment(result.convertedDate).tz(tz);
      }
    } else if (isFullDateTime && bookingTimeSlot) {
      convertedMoment = moment.tz(bookingTimeSlot, bookingTimezone || 'Asia/Calcutta').tz(tz);
    } else {
      const result = convertBookingTimeWithUserTimezone(
        bookingDate,
        bookingTimeSlot || '',
        bookingTimezone || 'Asia/Calcutta',
        tz
      );
      convertedMoment = moment(result.convertedDate).tz(tz);
    }

    const formattedDate = convertedMoment.format(
      weekdayFormat === 'long' ? 'dddd, D MMMM YYYY' : 'ddd, D MMMM YYYY'
    );
    const timeOnly = glueTimeMeridiem(
      formatTimeSlot(
        bookingDate,
        bookingTimeSlot || '',
        tz,
        bookingTimezone,
        durationMinutes,
        endDateTime
      )
    );

    if (includeDuration && durationMinutes != null && durationMinutes > 0) {
      return glueTimeMeridiem(`${formattedDate} | ${timeOnly} (${durationMinutes} min)`);
    }
    return glueTimeMeridiem(`${formattedDate} | ${timeOnly}`);
  } catch (error) {
    console.error('Error formatting booking date/time display:', error);
    return '-';
  }
};

/** Normalize getdetailId / list item into fields used by reschedule link UI. */
export const normalizeLinkedBookingForDisplay = (raw: any, fallbackId?: string) => {
  if (!raw) return null;
  return {
    ...raw,
    bookingId: raw.bookingId || raw.id || fallbackId || '',
    consultationTitle: raw.consultationTitle || raw.title || '',
    bookingTimeSlot: raw.bookingTimeSlot || raw.startDateTime || raw.bookingTime || '',
    bookingDate:
      raw.bookingDate ||
      raw.startDate ||
      raw.date ||
      (typeof raw.startDateTime === 'string' && raw.startDateTime.includes('T')
        ? raw.startDateTime.slice(0, 10)
        : ''),
    timezone: raw.timezone || raw.bookingTimezone || raw.timeZone || raw.bookingTimeZone || '',
    endDateTime: raw.endDateTime,
    timeInMinutes: raw.timeInMinutes,
  };
};

